import axios from 'axios';

// Use the full URL for debugging
//const API_URL = 'https://yvhsjs.realhost-free.net/api'; 
const API_URL = 'https://nd69zr.realhost-free.net/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Add withCredentials for CORS with credentials
  withCredentials: true
});

// Add a response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('API Error:', error.response.data);
      console.error('Status:', error.response.status);
      console.error('Headers:', error.response.headers);
      
      // Handle 401 Unauthorized errors
      if (error.response.status === 401) {
        console.log('401 Unauthorized error detected - dispatching unauthorized-access event');
        // Clear token
        localStorage.removeItem('token');
        // Dispatch the event that AuthGuard is listening for
        document.dispatchEvent(new Event('unauthorized-access'));
      }
      
      // Handle 402 Payment Required for Pro gating
      if (error.response.status === 402) {
        try {
          const data = error.response.data;
          if (data && (data.error === 'PRO_REQUIRED' || data.reason === 'FREE_QUOTA_EXHAUSTED')) {
            // Inform app to open upgrade flow
            window.dispatchEvent(new CustomEvent('pro-required', { detail: data }));
          }
        } catch (_) { /* ignore */ }
      }
      
    } else if (error.request) {
      // The request was made but no response was received
      console.error('Network Error:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Request Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Add a request interceptor to include the JWT token in requests
api.interceptors.request.use(
  (config) => {
    console.log(`Making request to: ${config.method.toUpperCase()} ${config.url}`);
    
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Authentication services
export const userService = {
  getUser: async () => {
    try {
      console.log('Attempting get User:');
      const response = await api.get('/user');
      console.log('GetUser response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },
  
  getCurrentUser: async () => {
    // For backwards compatibility, just call getUser
    return await userService.getUser();
  },
  updateEducationDegree: async (educationDegree) => {
    const response = await api.put('/user/education-degree', { educationDegree });
    return response.data;
  },
  updateProfile: async (payload) => {
    const response = await api.put('/user/profile', payload);
    return response.data;
  }
}

// Authentication services
export const authService = {
  login: async (email, password) => {
    try {
      console.log('Attempting login with:', { email });
      const response = await api.post('/auth/login', { email, password });
      console.log('Login response:', response.data);
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        // Dispatch a custom event to notify the app that auth state changed
        window.dispatchEvent(new Event('auth-change'));
      }
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },
  
  register: async (username, email, password) => {
    try {
      console.log('Attempting registration with:', { username, email });
      const response = await api.post('/auth/register', { username, email, password });
      console.log('Registration response:', response.data);
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        // Dispatch a custom event to notify the app that auth state changed
        window.dispatchEvent(new Event('auth-change'));
      }
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  },
  
  googleLogin: async (googleId, name, email) => {
    try {
      const response = await api.post('/auth/google-login', { googleId, name, email });
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        // Dispatch a custom event to notify the app that auth state changed
        window.dispatchEvent(new Event('auth-change'));
      }
      return response.data;
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  },
  
  logout: () => {
    localStorage.removeItem('token');
    // Dispatch a custom event to notify the app that auth state changed
    window.dispatchEvent(new Event('auth-change'));
  },
  
  isAuthenticated: () => {
    return localStorage.getItem('token') !== null;
  }
};

// Knowledge Assessment services
export const assessmentService = {
  // Create a knowledge assessment for a goal
  createKnowledgeAssessment: async () => {
    try {
      console.log('Creating knowledge assessment for active goal');
      const response = await api.post('/assessments/knowledge/create');
      console.log('Assessment response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating knowledge assessment:', error);
      throw error;
    }
  },
  
  createKnowledgeAssessmentForGoal: async (goalData) => {
    try {
      if (typeof goalData === 'object' && goalData.goalDescription) {
        // New format: passing goal description and level
        console.log(`Creating knowledge assessment for goal description: ${goalData.goalDescription}`);
        const response = await api.post('/assessments/knowledge/create-for-goal', {
          goalDescription: goalData.goalDescription,
          level: goalData.level || 'Beginner'
        });
        console.log('Assessment response:', response.data);
        return response.data;
      } else {
        // Legacy format: passing goal ID (for backward compatibility)
        const goalId = goalData;
        console.log(`Creating knowledge assessment for goal ID: ${goalId}`);
        const response = await api.post(`/assessments/knowledge/create-for-goal/${goalId}`);
        console.log('Assessment response:', response.data);
        return response.data;
      }
    } catch (error) {
      console.error('Error creating knowledge assessment:', error);
      throw error;
    }
  },
  
  getKnowledgeAssessment: async (id) => {
    try {
      const response = await api.get(`/assessments/knowledge/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching knowledge assessment ${id}:`, error);
      throw error;
    }
  },
  
  getKnowledgeAssessmentByGoalId: async (goalId) => {
    try {
      console.log(`Fetching knowledge assessments for goal ID: ${goalId}`);
      const response = await api.get(`/assessments/knowledge/goal/${goalId}`);
      console.log('Assessment by goal ID response:', response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching knowledge assessments for goal ${goalId}:`, error);
      return [];
    }
  },
  
  getUserAssessments: async () => {
    try {
      const response = await api.get('/assessments/knowledge/user');
      return response.data;
    } catch (error) {
      console.error('Error fetching user assessments:', error);
      return [];
    }
  },
  
  // Submit answers to a knowledge assessment
  submitKnowledgeAssessment: async (assessmentId, answers) => {
    try {
      console.log(`Submitting answers for assessment ID: ${assessmentId}`);
      const response = await api.post(`/assessments/knowledge/${assessmentId}/submit`, {
        answers: answers
      });
      console.log('Assessment submission response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error submitting knowledge assessment:', error);
      throw error;
    }
  },

  // Evaluate knowledge assessment and get topic recommendations
  evaluateKnowledgeAssessment: async (assessmentId, userAnswers) => {
    try {
      console.log(`Evaluating assessment ID: ${assessmentId}`);
      const response = await api.post(`/assessments/knowledge/${assessmentId}/evaluate`, {
        userAnswers: userAnswers
      });
      console.log('Assessment evaluation response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error evaluating knowledge assessment:', error);
      throw error;
    }
  }
};

// Goal services
export const goalService = {
  getGoals: async () => {
    const response = await api.get('/goals');
    return response.data;
  },
  
  getActiveGoal: async () => {
    try { 
      const response = await api.get('/goals/active');
      const g = response.data || null;
      if (!g) return null;
      const normalized = {
        ...g,
        skills: Array.isArray(g.Skills) ? g.Skills : (Array.isArray(g.skills) ? g.skills : []),
        goalProgress: g.GoalProgress || g.goalProgress || null,
        progress: g.Progress ?? g.progress ?? 0,
        // Normalize courses for consistent frontend usage
        courses: g.Course ? [{
          id: g.Course.Id || g.Course.id,
          title: g.Course.Title || g.Course.title,
          complexityLevel: g.Course.ComplexityLevel || g.Course.complexityLevel,
          imageUrl: g.Course.ImageUrl || g.Course.imageUrl,
          description: g.Course.Description || g.Course.description,
          desiredDuration: g.Course.DesiredDuration || g.Course.desiredDuration,
          realisticDuration: g.Course.RealisticDuration || g.Course.realisticDuration,
          tags: g.Course.Tags || g.Course.tags || [],
          isCompleted: g.Course.IsCompleted || g.Course.isCompleted || false,
          createdAt: g.Course.CreatedAt || g.Course.createdAt
        }] : []
      };
      console.log("GoalService active goal (normalized):", normalized);
      return normalized;
    } catch (error) {
      console.warn('Failed to fetch active goal from API', error);
      return null;
    }
  },
  
  getUserGoals: async () => {
    try { 
      const response = await api.get('/goals');
      const goals = response.data || [];
      const normalized = goals.map(g => ({
        ...g,
        skills: Array.isArray(g.Skills) ? g.Skills : (Array.isArray(g.skills) ? g.skills : []),
        goalProgress: g.GoalProgress || g.goalProgress || null,
        progress: g.Progress ?? g.progress ?? 0,
        // Normalize courses for consistent frontend usage
        courses: g.Course ? [{
          id: g.Course.Id || g.Course.id,
          title: g.Course.Title || g.Course.title,
          complexityLevel: g.Course.ComplexityLevel || g.Course.complexityLevel,
          imageUrl: g.Course.ImageUrl || g.Course.imageUrl,
          description: g.Course.Description || g.Course.description,
          desiredDuration: g.Course.DesiredDuration || g.Course.desiredDuration,
          realisticDuration: g.Course.RealisticDuration || g.Course.realisticDuration,
          tags: g.Course.Tags || g.Course.tags || [],
          isCompleted: g.Course.IsCompleted || g.Course.isCompleted || false,
          createdAt: g.Course.CreatedAt || g.Course.createdAt
        }] : []
      }));
      console.log("GoalService userGoals (normalized):", normalized);
      return normalized;
    } catch (error) {
      console.warn('Failed to fetch user goals from API', error);
      return [];
    }
  },

  // Journey overview: all goals with courses and progress in one call
  getGoalsOverview: async () => {
    try {
      const response = await api.get('/goals/overview');
      const goals = response.data || [];
      return goals.map(g => ({
        ...g,
        courses: (() => {
          const courseData = g.Course || g.Courses || g.courses;
          if (!courseData) return [];
          if (Array.isArray(courseData)) {
            return courseData.map(c => ({
              id: c.Id || c.id,
              title: c.Title || c.title,
              complexityLevel: c.ComplexityLevel || c.complexityLevel,
              imageUrl: c.ImageUrl || c.imageUrl,
              description: c.Description || c.description,
              desiredDuration: c.DesiredDuration || c.desiredDuration,
              realisticDuration: c.RealisticDuration || c.realisticDuration,
              tags: c.Tags || c.tags || [],
              isCompleted: c.IsCompleted || c.isCompleted || false,
              createdAt: c.CreatedAt || c.createdAt
            }));
          } else {
            // Single course
            const c = courseData;
            return [{
              id: c.Id || c.id,
              title: c.Title || c.title,
              complexityLevel: c.ComplexityLevel || c.complexityLevel,
              imageUrl: c.ImageUrl || c.imageUrl,
              description: c.Description || c.description,
              desiredDuration: c.DesiredDuration || c.desiredDuration,
              realisticDuration: c.RealisticDuration || c.realisticDuration,
              tags: c.Tags || c.tags || [],
              isCompleted: c.IsCompleted || c.isCompleted || false,
              createdAt: c.CreatedAt || c.createdAt
            }];
          }
        })(),
        goalProgress: g.GoalProgress || g.goalProgress || null,
        progress: g.Progress ?? g.progress ?? 0
      }));
    } catch (error) {
      console.warn('Failed to fetch goals overview from API', error);
      return [];
    }
  },
  
  getGoal: async (id) => {
    const response = await api.get(`/goals/${id}`);
    return response.data;
  },
  
  createGoal: async (description, metadata = null) => {
    try {
      const response = await api.post('/goals', { 
        description, 
        metadata 
      });
      return response.data;
    } catch (error) {
      console.error('Error creating goal:', error);
      // Disable rate limit propagation to avoid gating access for free plan
      // if (error.response && error.response.status === 429) { ... }
      throw error; // keep generic error behavior
    }
  },

  // Create goal with clarifications and courses (planned goal submit)
  // Accepts either courses (preferred) or legacy skills array
  createGoalWithPlan: async ({ description, clarifications = [], courses = [], skills = [] }) => {
    const payload = {
      description,
      clarifications,
      courses: (Array.isArray(courses) && courses.length > 0) ? courses : (Array.isArray(skills) ? skills : [])
    };
    const response = await api.post('/goals/with-plan', payload);
    return response.data;
  },
  
  updateGoal: async (id, description, isActive = true, metadata = null) => {
    const response = await api.put(`/goals/${id}`, { 
      description,
      isActive,
      metadata
    });
    return response.data;
  },
  
  setActiveGoal: async (id) => {
    const response = await api.put(`/goals/${id}/set-active`);
    return response.data;
  },
  
  completeGoal: async (id) => {
    const response = await api.put(`/goals/${id}/complete`);
    return response.data;
  },
  
  deleteGoal: async (id) => {
    const response = await api.delete(`/goals/${id}`);
    return response.data;
  },
  
  // Plan goals and skills using LLM (supports iterative clarifications)
  planGoals: async (goalDescription, clarifications = []) => {
    const payload = { goal: goalDescription, clarifications: clarifications.map(c => ({ question: c.question, answer: c.answer })) };
    const response = await api.post('/goals/plan', payload);
    console.log('Plan goals response:', response.data);
    console.log('Response structure check - ClarifyingQuestion:', response.data?.ClarifyingQuestion);
    console.log('Response structure check - Goal:', response.data?.Goal);
    return response.data;
  },
  
  getGoalRoadmaps: async (id) => {
    const response = await api.get(`/goals/${id}/roadmaps`);
    return response.data;
  },
  
  generateGoalImage: async (goalId) => {
    try {
      const response = await api.post(`/goals/${goalId}/generate-image`);
      return response.data;
    } catch (error) {
      console.error('Error generating goal image:', error);
      throw error;
    }
  },
  
  generateKnowledgeAssessment: async (id) => {
    const response = await api.post(`/goals/${id}/knowledge-assessment`);
    return response.data;
  },
  
  // Prefetch assessments for candidate skills
   // prefetchAssessments removed
}; // <-- closes goalService object

// Course services
export const courseService = {
  getUserCourses: async () => {
    try {
      console.log('Fetching user courses');
      const response = await api.get('/courses/my-courses');
      console.log('User courses response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching user courses:', error);
      throw error;
    }
  },

  getActiveCourses: async () => {
    try {
      console.log('Fetching active courses');
      const response = await api.get('/courses/active');
      console.log('Active courses response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching active courses:', error);
      throw error;
    }
  },

  setActiveCourse: async (courseId) => {
    try {
      console.log('Setting course as active:', courseId);
      const response = await api.put(`/courses/${courseId}/set-active`);
      console.log('Set active course response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error setting course as active:', error);
      throw error;
    }
  }
};

// Learning plan services
export const learningPlanService = {
  getLearningPlan: async (id) => {
    const response = await api.get(`/learningplans/${id}`);
    return response.data;
  },
  
  getLearningPlanByUserId: async (userId) => {
    const response = await api.get(`/learningplans/user/${userId}`);
    return response.data;
  },
  
  getLearningPlanByGoalId: async (goalId) => {
    const response = await api.get(`/learningplans/goal/${goalId}`);
    return response.data;
  },
  
  createLearningPlan: async (goalId, roadmapId, assessmentResults, duration) => {
    const response = await api.post('/learningplans', {
      goalId,
      roadmapId,
      assessmentResults,
      duration
    });
    return response.data;
  },
  
  // Method to call the CreateLearningPlan endpoint without parameters
  regenerateLearningPlan: async () => {
    const response = await api.post('/learningplans');
    return response.data;
  },

  regenerateRoadmap: async (learningPlanId) => {
    try {
      console.log(`Attempting to regenerate roadmap for learning plan ID: ${learningPlanId}`);
      const response = await api.post(`/learningplans/${learningPlanId}/regenerate-roadmap`);
      console.log('Regenerate roadmap response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Regenerate roadmap error:', error);
      throw error;
    }
  },
  
  updateRoadmapCompletion: async (learningPlanId, completionStatus) => {
    const response = await api.put(`/learningplans/${learningPlanId}/roadmap-completion`, {
      completionStatus: JSON.stringify(completionStatus)
    });
    return response.data;
  },
  
  getUserLearningPlans: async () => {
    try {
      const response = await api.get('/learningplans');
      return response.data;
    } catch (error) {
      console.warn('Failed to fetch learning plans, returning empty array', error);
      return [];
    }
  },
  
  getCurrentPlan: async () => {
    try {
      // First, get all user plans
      const plans = await api.get('/learningplans');
      
      // If there are plans, return the most recent one
      if (plans.data && plans.data.length > 0) {
        // Sort by createdAt in descending order
        const sortedPlans = plans.data.sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        );
        return sortedPlans[0];
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch current learning plan:', error);
      throw error;
    }
  },

  // Update the optimized data loading function to handle roadmaps directly
  getOptimizedLearningPlanData: async (courseId = null) => {
    try {
      console.log('Fetching optimized learning plan data');

      // Build the URL with optional courseId
      const url = courseId
        ? `/learningplan/optimized-data/${courseId}`
        : '/learningplan/optimized-data';

      const response = await api.get(url);
      
      // Debug log the actual response
      console.log('Optimized API raw response:', response);
      console.log('Optimized API response.data:', response.data);
      
      // Check the data structure and normalize if needed
      if (response.data) {
        const data = response.data;
        
        // Deep inspect the first roadmap for debugging
        if (data.Roadmaps && data.Roadmaps.length > 0) {
          console.log('First roadmap from API:', data.Roadmaps[0]);
          console.log('First roadmap properties:', Object.keys(data.Roadmaps[0]));
          
          // Check content property
          const firstItem = data.Roadmaps[0];
          console.log('Content property value:', firstItem.Content || firstItem.content);
        }
        
        // Return a normalized structure with consistent property casing
        return {
          User: data.User || data.user || {},
          Roadmaps: normalizeRoadmaps(data.Roadmaps || data.roadmaps || []),
          LessonsForRoadmaps: normalizeLessons(data.LessonsForRoadmaps || data.lessonsForRoadmaps || {}),
          FirstUncompletedLessonRoadmapId: data.FirstUncompletedLessonRoadmapId || data.firstUncompletedLessonRoadmapId,
          FirstUncompletedLesson: normalizeLesson(data.FirstUncompletedLesson || data.firstUncompletedLesson || {}),
          Courses: data.Courses || data.courses || []
        };
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching optimized learning plan data:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  },
  
  // Update the super fast data loading function to handle roadmaps directly
  // Local storage helpers for course management
  getLastOpenedCourseId: () => {
    try {
      return localStorage.getItem('lastOpenedCourseId');
    } catch (error) {
      console.warn('Error reading from localStorage:', error);
      return null;
    }
  },

  setLastOpenedCourseId: (courseId) => {
    try {
      localStorage.setItem('lastOpenedCourseId', courseId);
      console.log('Saved last opened course ID:', courseId);
    } catch (error) {
      console.warn('Error writing to localStorage:', error);
    }
  },

  getSuperFastLearningPlanData: async (courseId) => {
    try {
      console.log('Using super fast learning plan data loading strategy with courseId:', courseId);

      // Check if courseId is provided
      if (!courseId) {
        console.warn('No courseId provided to getSuperFastLearningPlanData');
        throw new Error('Course ID is required for learning plan data');
      }

      // First try the optimized endpoint
      try {
        const optimizedData = await learningPlanService.getOptimizedLearningPlanData(courseId || null);
        console.log('Successfully retrieved optimized data:', optimizedData);
        
        // Normalize data to ensure consistent property casing
        const normalizedData = {
          User: optimizedData.User || optimizedData.user || {},
          Roadmaps: normalizeRoadmaps(optimizedData.Roadmaps || optimizedData.roadmaps || []),
          LessonsForRoadmaps: normalizeLessons(optimizedData.LessonsForRoadmaps || optimizedData.lessonsForRoadmaps || {}),
          FirstUncompletedLessonRoadmapId: optimizedData.FirstUncompletedLessonRoadmapId || optimizedData.firstUncompletedLessonRoadmapId,
          FirstUncompletedLesson: optimizedData.FirstUncompletedLesson || optimizedData.firstUncompletedLesson,
          Courses: optimizedData.Courses || optimizedData.courses || []
        };
        
        console.log('Normalized data:', normalizedData);
        return normalizedData;
      } catch (optimizedError) {
        console.warn('Optimized endpoint failed, falling back to standard endpoint:', optimizedError);

        // Fallback to standard endpoint with courseId
        console.log('Attempting to use standard data endpoint as fallback');
        const response = await api.get(`/learningplan/data/${courseId}`);
        console.log('Successfully retrieved data from standard endpoint');
        return response.data;
      }
    } catch (error) {
      console.error('All data loading strategies failed:', error);
      throw error;
    }
  },

  // New optimized method for getting user's courses and learning data
  getUserCoursesAndLearningData: async () => {
    try {
      console.log('Fetching user courses and learning data (optimized)');
      const response = await api.get('/learningplan/user-courses-data');
      return response.data;
    } catch (error) {
      console.error('Error fetching user courses and learning data:', error);
      throw error;
    }
  },

  // New optimized method for getting course-specific learning plan data
  getCourseLearningPlanData: async (courseId) => {
    try {
      console.log('Fetching course learning plan data for course:', courseId);
      const response = await api.get(`/learningplan/course-data/${courseId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching course learning plan data:', error);
      throw error;
    }
  },
};

// Helper function to normalize roadmaps property names
function normalizeRoadmaps(items) {
  return items.map(item => {
    // Log the item structure to debug
    console.log('Normalizing roadmap:', item);
    console.log('Item properties:', Object.keys(item));
    
    return {
      Id: item.Id || item.id,
      GoalId: item.GoalId || item.goalId,
      Name: item.Name || item.name,
      Content: item.Content || item.content,
      IsCompleted: item.IsCompleted || item.isCompleted || false
    };
  });
}

// Helper function to normalize a single lesson
function normalizeLesson(lesson) {
  if (!lesson || typeof lesson !== 'object') return null;
  
  return {
    Id: lesson.Id || lesson.id,
    Title: lesson.Title || lesson.title,
    RoadmapId: lesson.RoadmapId || lesson.roadmapId,
    IsCompleted: lesson.IsCompleted || lesson.isCompleted || false,
    Grade: lesson.Grade || lesson.grade,
    // Keep EstimatedTimeInMinutes property for use in Lesson component
    EstimatedTimeInMinutes: lesson.EstimatedTimeInMinutes || lesson.estimatedTimeInMinutes || 30,
    LessonAssessment: lesson.LessonAssessment ? {
      Id: lesson.LessonAssessment.Id || lesson.LessonAssessment.id,
      Score: lesson.LessonAssessment.Score || lesson.LessonAssessment.score
    } : null
  };
}

// Helper function to normalize lessons property names
function normalizeLessons(lessonsMap) {
  const normalizedMap = {};
  
  Object.keys(lessonsMap).forEach(key => {
    normalizedMap[key] = lessonsMap[key].map(lesson => {
      return {
        Id: lesson.Id || lesson.id,
        Title: lesson.Title || lesson.title,
        RoadmapId: lesson.RoadmapId || lesson.roadmapId,
        IsCompleted: lesson.IsCompleted || lesson.isCompleted || false,
        Grade: lesson.Grade || lesson.grade,
        // Keep EstimatedTimeInMinutes property for use in Lesson component, even if not needed in LearningPlan
        EstimatedTimeInMinutes: lesson.EstimatedTimeInMinutes || lesson.estimatedTimeInMinutes || 30,
        LessonAssessment: lesson.LessonAssessment ? {
          Id: lesson.LessonAssessment.Id || lesson.LessonAssessment.id,
          Score: lesson.LessonAssessment.Score || lesson.LessonAssessment.score
        } : null
      };
    });
  });
  
  return normalizedMap;
}

// Roadmap services
export const roadmapService = {
  getRoadmaps: async (userId) => {
    try {
      const response = await api.get(`/roadmaps/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch roadmaps:', error);
      // Return empty array instead of null to indicate no roadmaps exist
      return [];
    }
  },
  
  getRoadmapsByGoalId: async (goalId) => {
    try {
      const response = await api.get(`/roadmaps/goal/${goalId}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch roadmaps for goal ID ${goalId}:`, error);
      // Return empty array instead of null to indicate no roadmaps exist
      return [];
    }
  },
  
  getRoadmap: async (userId) => {
    try {
      const roadmaps = await roadmapService.getRoadmaps(userId);
      // For backward compatibility, return the first roadmap if any exist
      return roadmaps.length > 0 ? roadmaps[0] : null;
    } catch (error) {
      console.error('Failed to fetch roadmap:', error);
      return null;
    }
  },
  
  getRoadmapItems: async (roadmapId) => {
    try {
      const response = await api.get(`/roadmaps/${roadmapId}/items`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch roadmap items for roadmap ID ${roadmapId}:`, error);
      return [];
    }
  },
  
  createRoadmaps: async (selectedTopics = null, courseId = null) => {
    try {
      console.log(`Attempting to create roadmaps for course ID: ${courseId}`);

      // Prepare request body with selected topics and course ID if provided
      const requestBody = {
        ...(selectedTopics && selectedTopics.length > 0 && { SelectedTopics: selectedTopics }),
        ...(courseId && { CourseId: courseId })
      };

      // Use courseId in URL (new format: userId/courseId)
      const urlPath = `${courseId || ''}`;
      const response = await api.post(`/roadmaps/${urlPath}`, requestBody);
      console.log('Create roadmaps response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Create roadmaps error:', error);
      // Disable rate limit propagation for roadmap creation
      throw error;
    }
  },
  
  updateRoadmapItemCompletion: async (itemId, isCompleted) => {
    try {
      console.log(`Updating completion status for roadmap item ID: ${itemId} to ${isCompleted}`);
      const response = await api.put(`/roadmaps/items/${itemId}/completion`, isCompleted);
      return response.data;
    } catch (error) {
      console.error('Update roadmap item completion error:', error);
      throw error;
    }
  },
  
  updateCompletionStatus: async (roadmapId, isCompleted) => {
    try {
      console.log(`Updating completion status for roadmap ID: ${roadmapId} to ${isCompleted}`);
      const response = await api.put(`/roadmaps/${roadmapId}/completion`, isCompleted);
      return response.data;
    } catch (error) {
      console.error('Update roadmap completion error:', error);
      throw error;
    }
  },
  
  associateRoadmapWithGoal: async (roadmapId, goalId) => {
    try {
      console.log(`Associating roadmap ID: ${roadmapId} with goal ID: ${goalId}`);
      const response = await api.put(`/roadmaps/${roadmapId}/goal/${goalId}`);
      return response.data;
    } catch (error) {
      console.error('Associate roadmap with goal error:', error);
      throw error;
    }
  },
  
  removeGoalFromRoadmap: async (roadmapId) => {
    try {
      console.log(`Removing goal from roadmap ID: ${roadmapId}`);
      const response = await api.put(`/roadmaps/${roadmapId}/remove-goal`);
      return response.data;
    } catch (error) {
      console.error('Remove goal from roadmap error:', error);
      throw error;
    }
  },
  
  getRoadmapWithLessons: async (roadmapId) => {
    try {
      const response = await api.get(`/roadmaps/${roadmapId}/with-lessons`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch roadmap with lessons for roadmap ID ${roadmapId}:`, error);
      return null;
    }
  },
  
  updateRoadmapCompletion: async (roadmapId, isCompleted) => {
    try {
      console.log(`Updating completion status for roadmap ID: ${roadmapId} to ${isCompleted}`);
      const response = await api.put(`/roadmaps/${roadmapId}/completion`, isCompleted);
      return response.data;
    } catch (error) {
      console.error('Update roadmap completion error:', error);
      throw error;
    }
  },
  
  deleteAllRoadmaps: async (goalId) => {
    try {
      console.log(`Deleting all roadmaps for goal ID: ${goalId}`);
      const response = await api.delete(`/roadmaps/deleteAll/${goalId}`);
      return response.data;
    } catch (error) {
      console.error('Delete all roadmaps error:', error);
      throw error;
    }
  },
};

// Lesson services
export const lessonService = {
  getLesson: async (id) => {
    try {
      const response = await api.get(`/lessons/${id}`);
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching lesson ${id}:`, error);
      throw error;
    }
  },
  
  getLessonsByLearningPlanId: async (learningPlanId) => {
    const response = await api.get(`/lessons/learning-plan/${learningPlanId}`);
    return response.data;
  },
  
  getLessonsByRoadmapId: async (roadmapId) => {
    const response = await api.get(`/lessons/roadmap/${roadmapId}`);
    return response.data;
  },
  
  getLessonsForToday: async (learningPlanId) => {
    const response = await api.get(`/lessons/today/${learningPlanId}`);
    return response.data;
  },
  
  getCurrentLesson: async () => {
    const response = await api.get('/lessons/currentLesson');
    return response.data;
  },
  
  getNextLesson: async (lessonId) => {
    try {
      const response = await api.get(`/lessons/${lessonId}/next-lesson`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching next lesson for lesson ID: ${lessonId}`, error);
      // If we get a 404, it means this is the last lesson in the roadmap item
      if (error.response && error.response.status === 404) {
        return null;
      }
      throw error;
    }
  },
  
  generateLessonContent: async (id) => {
    try {
      const response = await api.post(`/lessons/${id}/generate-content`);
      return response.data;
    } catch (error) {
      console.error(`Error generating content for lesson ${id}:`, error);
      
      // Check if it's a rate limit error
      if (error.response && error.response.status === 429) {
        const errorData = error.response.data;
        // Throw a custom error with rate limit information
        const rateLimitError = new Error('RATE_LIMIT_EXCEEDED');
        rateLimitError.isRateLimit = true;
        rateLimitError.details = {
          message: errorData.message || 'Rate limit exceeded',
          hoursUntilReset: errorData.hoursUntilReset || 24,
          subscriptionMessage: errorData.subscriptionMessage || 'Only 1 lesson per 24 hours allowed in free version',
          currentCount: errorData.currentCount || 1,
          maxRequests: errorData.maxRequests || 1
        };
        throw rateLimitError;
      }
      
      // For other errors, throw them as usual
      throw error;
    }
  },
  
  generateLessonImage: async (id) => {
    try {
      const response = await api.post(`/lessons/${id}/generate-image`);
      return response.data;
    } catch (error) {
      console.error(`Error generating image for lesson ${id}:`, error);
      throw error;
    }
  },
  
  checkLessonImageStatus: async (id) => {
    try {
      const response = await api.get(`/lessons/${id}/image-status`);
      return response.data;
    } catch (error) {
      console.error(`Error checking image status for lesson ${id}:`, error);
      return {
        hasImage: false,
        imageUrl: null,
        hasImagePrompt: false
      };
    }
  },
  
  generateLessonAssessment: async (id) => {
    try {
      console.log(`Generating assessment for lesson ID: ${id}`);
      const response = await api.post(`/lessons/${id}/assessment`);
      
      // Log the response details for debugging
      console.log('Assessment generation response:', response.data);
      
      // Ensure the response is well-formed
      if (response.data && response.data.questions) {
        return response.data;
      } else if (response.data && response.data.Questions) {
        // Normalize property name casing
        return {
          id: response.data.Id || response.data.id,
          questions: response.data.Questions
        };
      } else {
        console.warn('Assessment response missing questions:', response.data);
        // Create a default structure
        return {
          id: response.data?.Id || response.data?.id || 0,
          questions: []
        };
      }
    } catch (error) {
      // Add better error handling with detailed error logging
      console.error('Error generating lesson assessment:', error);
      console.error('Error details:', error.response?.data || 'No response data available');
      
      // Rethrow the error with the response data if available
      if (error.response && error.response.data) {
        throw new Error(typeof error.response.data === 'string' ? 
          error.response.data : 
          JSON.stringify(error.response.data));
      }
      
      throw error;
    }
  },
  
  regenerateAssessment: async (lessonId) => {
    try {
      console.log(`Regenerating assessment for lesson ID: ${lessonId}`);
      
      // First, try to delete any existing assessment
      try {
        // Get existing assessment
        const existingAssessment = await lessonService.getLessonAssessment(lessonId);
        
        // If there's an existing assessment, delete it
        if (existingAssessment && existingAssessment.id) {
          console.log(`Deleting existing assessment ID: ${existingAssessment.id}`);
          await api.delete(`/assessments/lesson/${existingAssessment.id}`);
        }
      } catch (deleteError) {
        // Just log the error but continue with regeneration
        console.warn('Error while trying to delete existing assessment:', deleteError);
      }
      
      // Generate a new assessment
      return await lessonService.generateLessonAssessment(lessonId);
    } catch (error) {
      console.error('Error regenerating assessment:', error);
      throw error;
    }
  },
  
  getLessonAssessment: async (lessonId) => {
    try {
      // If nothing in localStorage, proceed with API calls
      console.log(`Querying API for assessments for lesson ID: ${lessonId}`);
      
      // Get all assessments for this lesson
      const assessmentsResponse = await api.get(`/assessments/lesson/${lessonId}`);
      
      // Check if we got a valid response with assessments
      if (assessmentsResponse.data && Array.isArray(assessmentsResponse.data) && assessmentsResponse.data.length > 0) {
        console.log(`Found ${assessmentsResponse.data.length} existing assessments in API:`, assessmentsResponse.data);
        
        // Return the most recent assessment
        const mostRecentAssessment = assessmentsResponse.data[0]; // Assume the first one is the latest
        
        // Ensure consistent property naming
        return {
          id: mostRecentAssessment.id || mostRecentAssessment.Id,
          lessonId: parseInt(lessonId),
          questions: mostRecentAssessment.questions || mostRecentAssessment.Questions,
          answers: mostRecentAssessment.answers || mostRecentAssessment.Answers || "[]",
          isCompleted: mostRecentAssessment.isCompleted || mostRecentAssessment.IsCompleted || false,
          createdAt: mostRecentAssessment.createdAt || mostRecentAssessment.CreatedAt
        };
      }
      
      // No existing assessments found
      console.log('No existing assessments found in API, will need to generate a new one');
      return null;
    } catch (error) {
      console.error(`Failed to get assessment for lesson ID: ${lessonId}`, error);
      
      // Be explicit about errors to help with debugging
      if (error.response) {
        console.error('Server responded with error:', error.response.status, error.response.data);
      } else if (error.request) {
        console.error('No response received from server');
      } else {
        console.error('Error setting up the request:', error.message);
      }
      
      throw error;
    }
  },
  
  submitLessonAssessment: async (assessmentId, answers, grade = null) => {
    const response = await api.post(`/assessments/lesson/${assessmentId}/submit`, { 
      answers: answers,
      grade: grade
    });
    return response.data;
  },
  
  completeLesson: async (id, score) => {
    try {
      const response = await api.put(`/lessons/${id}/complete/${score}`);
      
      // Store the assessment data if it's included in the response
      if (response.data && response.data.Assessment) {
        // Store assessment data in localStorage so we don't need to fetch it again
        localStorage.setItem(`lesson_assessment_${id}`, JSON.stringify(response.data.Assessment));
      }
      
      return response.data;
    } catch (error) {
      // Propagate the error message from the server if available
      if (error.response && error.response.data) {
        console.error('Error completing lesson:', error.response.data);
        throw error;
      } else {
        console.error('Error completing lesson:', error.message);
        throw new Error('Failed to complete lesson. Please try again later.');
      }
    }
  },
  
  checkRoadmapCompletion: async (roadmapId) => {
    try {
      // Get all lessons for this roadmap
      const lessonsResponse = await api.get(`/lessons/roadmap/${roadmapId}`);
      const lessons = lessonsResponse.data;
      
      // Check if there are any lessons and if all of them are completed
      const allLessonsCompleted = lessons.length > 0 && lessons.every(l => l.isCompleted);
      
      if (allLessonsCompleted) {
        console.log(`All lessons completed for roadmap ${roadmapId}. Marking roadmap as completed.`);
        // Update roadmap completion status
        await roadmapService.updateRoadmapCompletion(roadmapId, true);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error(`Error checking completion status for roadmap ${roadmapId}:`, error);
      return false;
    }
  },
  
  generateLessonsForRoadmap: async (roadmapId) => {
    try {
      console.log(`Generating lessons for roadmap ID: ${roadmapId}`);
      
      // API endpoint to generate lessons for the roadmap
      const response = await api.post(`/roadmaps/${roadmapId}/generate-lessons`, {
        generateContent: false // Don't generate content, just the lesson titles
      });
      
      console.log(`Successfully generated lessons for roadmap ${roadmapId}:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error generating lessons for roadmap ${roadmapId}:`, error);
      throw error;
    }
  },
  
  generateLessonAudio: async (lessonId) => {
    try {
      const response = await api.post(`/audio/generate/${lessonId}`);
      return response.data;
    } catch (error) {
      console.error('Error generating lesson audio:', error);
      throw error;
    }
  },
  
  getLessonAudio: async (lessonId) => {
    try {
      const response = await api.get(`/lessons/${lessonId}/audio`);
      return response.data;
    } catch (error) {
      console.error('Error getting lesson audio:', error);
      throw error;
    }
  },
  
  // Wait for assessment generation to complete (with timeout)
  waitForAssessmentGeneration: async (lessonId, timeoutMs = 30000) => {
    const generationKey = `assessment_generating_${lessonId}`;
    const startTime = Date.now();
    
    while (localStorage.getItem(generationKey) === 'true') {
      if (Date.now() - startTime > timeoutMs) {
        throw new Error('Assessment generation timeout');
      }
      
      // Wait 500ms before checking again
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Check if assessment is now available
      try {
        const assessment = await lessonService.getLessonAssessment(lessonId);
        if (assessment && assessment.questions && assessment.questions.length > 0) {
          localStorage.removeItem(generationKey);
          return assessment;
        }
      } catch (error) {
        // Continue waiting if assessment fetch fails - assessment might not exist yet
        console.log('Assessment not yet available, continuing to wait...');
      }
    }
    
    // Generation completed, fetch the final assessment
    try {
      return await lessonService.getLessonAssessment(lessonId);
    } catch (error) {
      console.error('Failed to fetch assessment after generation completed:', error);
      // Return null if we can't fetch the assessment
      return null;
    }
  },
  
  // Fire-and-forget assessment generation - starts generation without waiting for completion
  startAssessmentGeneration: async (lessonId) => {
    try {
      console.log(`Starting fire-and-forget assessment generation for lesson ID: ${lessonId}`);
      
      // Check if assessment already exists
      try {
        const existingAssessment = await lessonService.getLessonAssessment(lessonId);
        if (existingAssessment && existingAssessment.questions && existingAssessment.questions.length > 0) {
          console.log('Assessment already exists, skipping generation');
          return;
        }
      } catch (error) {
        // If getLessonAssessment fails (e.g., no assessment exists), continue with generation
        console.log('No existing assessment found, proceeding with generation');
      }
      
      // Mark that generation is in progress
      const generationKey = `assessment_generating_${lessonId}`;
      if (localStorage.getItem(generationKey)) {
        console.log('Assessment generation already in progress');
        return;
      }
      
      localStorage.setItem(generationKey, 'true');
      
      // Fire-and-forget call - don't await the result
      lessonService.generateLessonAssessment(lessonId)
        .then(() => {
          console.log(`Assessment generation completed for lesson ${lessonId}`);
          localStorage.removeItem(generationKey);
        })
        .catch((error) => {
          console.error(`Assessment generation failed for lesson ${lessonId}:`, error);
          localStorage.removeItem(generationKey);
        });
      
    } catch (error) {
      console.error('Error starting assessment generation:', error);
    }
  },
  
  // Check if assessment generation is in progress
  isAssessmentGenerating: (lessonId) => {
    const generationKey = `assessment_generating_${lessonId}`;
    return localStorage.getItem(generationKey) === 'true';
  },
  
  // Wait for content generation to complete (with timeout)
  waitForContentGeneration: async (lessonId, timeoutMs = 60000) => {
    const generationKey = `content_generating_${lessonId}`;
    const startTime = Date.now();
    
    while (localStorage.getItem(generationKey) === 'true') {
      if (Date.now() - startTime > timeoutMs) {
        throw new Error('Content generation timeout');
      }
      
      // Wait 1 second before checking again (content generation takes longer)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Check if content is now available
      try {
        const lesson = await lessonService.getLesson(lessonId);
        if (lesson && lesson.content && lesson.content.trim() !== '') {
          localStorage.removeItem(generationKey);
          return lesson;
        }
      } catch (error) {
        // Continue waiting if lesson fetch fails - content might not exist yet
        console.log('Lesson content not yet available, continuing to wait...');
      }
    }
    
    // Generation completed, fetch the final lesson
    try {
      return await lessonService.getLesson(lessonId);
    } catch (error) {
      console.error('Failed to fetch lesson after content generation completed:', error);
      // Return null if we can't fetch the lesson
      return null;
    }
  },

  // Fire-and-forget lesson content generation - starts generation without waiting for completion
  startContentGeneration: async (lessonId) => {
    try {
      console.log(`Starting fire-and-forget content generation for lesson ID: ${lessonId}`);
      
      // Check if lesson already has content
      try {
        const lesson = await lessonService.getLesson(lessonId);
        if (lesson && lesson.content && lesson.content.trim() !== '') {
          console.log('Lesson already has content, skipping generation');
          return;
        }
      } catch (error) {
        // If getLesson fails, continue with generation attempt
        console.log('Could not check existing lesson content, proceeding with generation');
      }
      
      // Mark that generation is in progress
      const generationKey = `content_generating_${lessonId}`;
      if (localStorage.getItem(generationKey)) {
        console.log('Content generation already in progress');
        return;
      }
      
      localStorage.setItem(generationKey, 'true');
      
      // Fire-and-forget call - don't await the result
      lessonService.generateLessonContent(lessonId)
        .then(() => {
          console.log(`Content generation completed for lesson ${lessonId}`);
          localStorage.removeItem(generationKey);
        })
        .catch((error) => {
          console.error(`Content generation failed for lesson ${lessonId}:`, error);
          localStorage.removeItem(generationKey);
        });
      
    } catch (error) {
      console.error('Error starting content generation:', error);
    }
  },
  
  // Check if content generation is in progress
  isContentGenerating: (lessonId) => {
    const generationKey = `content_generating_${lessonId}`;
    return localStorage.getItem(generationKey) === 'true';
  },
};

// Note services
export const noteService = {
  getAllNotes: async () => {
    const response = await api.get('/notes');
    return response.data;
  },
  
  getNoteById: async (id) => {
    const response = await api.get(`/notes/${id}`);
    return response.data;
  },
  
  getNotesByLessonId: async (lessonId) => {
    const response = await api.get(`/notes/lesson/${lessonId}`);
    return response.data;
  },
  
  searchNotes: async (query) => {
    const response = await api.get(`/notes/search?q=${encodeURIComponent(query)}`);
    return response.data;
  },
  
  createNote: async (title, content, lessonId = null, tags = '') => {
    const response = await api.post('/notes', { 
      title, 
      content, 
      lessonId, 
      tags 
    });
    return response.data;
  },
  
  updateNote: async (id, title, content, tags = '') => {
    const response = await api.put(`/notes/${id}`, { 
      title,
      content, 
      tags 
    });
    return response.data;
  },
  
  deleteNote: async (id) => {
    const response = await api.delete(`/notes/${id}`);
    return response.data;
  },
  
  deleteAllNotes: async () => {
    const response = await api.delete('/notes/all');
    return response.data;
  },
  
  // Get note connections (for graph visualization)
  getNoteConnections: async () => {
    try {
      console.log('Requesting note connections from API');
      const response = await api.get('/notes/connections');
      console.log('Received note connections:', response.data);
      return response.data;
    } catch (error) {
      console.warn('Failed to fetch note connections from API, using fallback data', error);
      // Provide fallback empty connections array if the API fails
      return [];
    }
  },
  
  // Create a new connection between notes
  createNoteConnection: async (sourceId, targetId, strength = 1) => {
    try {
      const response = await api.post('/notes/connections', {
        sourceId,
        targetId,
        strength
      });
      return response.data;
    } catch (error) {
      console.error('Failed to create note connection:', error);
      throw error;
    }
  },
  
  // Delete a note connection
  deleteNoteConnection: async (id) => {
    try {
      const response = await api.delete(`/notes/connections/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete note connection:', error);
      throw error;
    }
  },
  
  importObsidianNotes: async (files) => {
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('files', file);
    });

    const response = await api.post('/notes/import/obsidian', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Chat with LLM service
export const chatService = {
  // Flag to determine if we should use the mock service or the real API
  // Change this to false now that the backend API is implemented
  _useMockApi: false,
  
  sendMessage: async (message) => {
    try {
      // If using mock API, generate a realistic response
      if (chatService._useMockApi) {
        console.log('Using mock LLM API response');
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate network delay
        
        // Generate a more realistic response based on the message content
        let response = '';
        
        if (message.toLowerCase().includes('hello') || message.toLowerCase().includes('hi')) {
          response = "Hello! I'm your Brainy AI assistant. I'm here to help you with your learning journey. What would you like to know about today?";
        } else if (message.toLowerCase().includes('learn') || message.toLowerCase().includes('study')) {
          response = "Learning is a lifelong journey! The most effective learning strategies often involve active recall, spaced repetition, and connecting new knowledge to existing concepts. Is there a particular subject you're interested in learning about?";
        } else if (message.toLowerCase().includes('computer') || message.toLowerCase().includes('programming')) {
          response = "Computer science and programming are fascinating fields! They involve understanding algorithms, data structures, and computational thinking. Are you interested in a specific programming language or concept?";
        } else if (message.toLowerCase().includes('math') || message.toLowerCase().includes('mathematics')) {
          response = "Mathematics is the foundation of many disciplines! From algebra and calculus to statistics and number theory, math provides powerful tools for solving problems. Which area of mathematics are you exploring?";
        } else if (message.toLowerCase().includes('science')) {
          response = "Science helps us understand the world through observation, experimentation, and theorizing. Whether it's physics, chemistry, biology, or another branch, scientific thinking is invaluable. What scientific topics are you curious about?";
        } else if (message.toLowerCase().includes('thank')) {
          response = "You're welcome! I'm happy to help. Feel free to ask if you have more questions as you continue your learning journey.";
        } else if (message.length < 10) {
          response = "I'd be happy to help with that. Could you provide a bit more detail about what you're looking for?";
        } else {
          response = "That's an interesting topic! Learning about " + message.split(' ').slice(0, 3).join(' ') + " and related subjects can expand your knowledge base significantly. Would you like me to recommend some resources or explain specific concepts related to this area?";
        }
        
        return { response };
      }
      
      // Use the real API
      const response = await api.post('/chat', { message });
      return response.data;
    } catch (error) {
      console.error('Chat error:', error);
      // If the API fails, provide a fallback message
      return { 
        response: "I'm sorry, I couldn't process your request. The chat service might be temporarily unavailable." 
      };
    }
  },
  
  getChatHistory: async (skip = 0, take = 10) => {
    try {
      // If using mock API, return empty history
      if (chatService._useMockApi) {
        console.log('Using mock chat history');
        return [];
      }
      
      // Use the real API
      const response = await api.get(`/chat/history?skip=${skip}&take=${take}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching chat history:', error);
      return [];
    }
  },
  
  clearChatHistory: async () => {
    try {
      // If using mock API, just return success
      if (chatService._useMockApi) {
        console.log('Using mock clear chat history');
        return { success: true, message: 'Chat history cleared' };
      }
      
      // Use the real API
      const response = await api.delete('/chat/history');
      return response.data;
    } catch (error) {
      console.error('Error clearing chat history:', error);
      throw error;
    }
  }
};

// Pomodoro timer services
export const pomodoroService = {
  // Create a new pomodoro session
  createSession: async (completedAt, duration, notes = '') => {
    try {
      console.log('Creating pomodoro session with:', {
        completedAt: completedAt instanceof Date ? completedAt.toISOString() : completedAt,
        duration,
        notes
      });
      
      // Ensure completedAt is an ISO string
      const formattedCompletedAt = completedAt instanceof Date 
        ? completedAt.toISOString() 
        : completedAt;
      
      const response = await api.post('/pomodoro/session', {
        completedAt: formattedCompletedAt,
        duration,
        notes
      });
      
      console.log('Create session response:', response.data);
      
      // Notify that a session was completed
      window.dispatchEvent(new CustomEvent('pomodoro-session-completed', {
        detail: { session: response.data }
      }));
      
      return response.data;
    } catch (error) {
      console.error('Failed to create pomodoro session:', error);
      throw error;
    }
  },
  
  // Get a specific pomodoro session
  getSession: async (id) => {
    try {
      const response = await api.get(`/pomodoro/session/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch pomodoro session ${id}:`, error);
      throw error;
    }
  },
  
  // Get all pomodoro sessions for the current user
  getSessions: async (startDate, endDate) => {
    try {
      let url = '/pomodoro/sessions';
      
      if (startDate && endDate) {
        url += `?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;
      }
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch pomodoro sessions:', error);
      throw error;
    }
  },
  
  // Get pomodoro stats for the current user
  getStats: async (startDate, endDate) => {
    try {
      let url = '/pomodoro/stats';
      
      // Format the dates for the query string
      if (startDate && endDate) {
        // Ensure proper date encoding for URL parameters
        const start = encodeURIComponent(startDate.toISOString());
        const end = encodeURIComponent(endDate.toISOString());
        url += `?startDate=${start}&endDate=${end}`;
      }
      
      console.log(`Making getStats request to: ${url}`);
      
      const response = await api.get(url);
      console.log('Pomodoro stats response:', response.data);
      
      return response.data || { 
        TotalSessions: 0, 
        TotalFocusTimeSeconds: 0 
      };
    } catch (error) {
      console.error('Failed to fetch pomodoro stats:', error);
      
      // Return default values instead of throwing, to make UI more resilient
      return { 
        TotalSessions: 0, 
        TotalFocusTimeSeconds: 0,
        error: error.message
      };
    }
  },
  
  // Delete a pomodoro session
  deleteSession: async (id) => {
    try {
      await api.delete(`/pomodoro/session/${id}`);
      return true;
    } catch (error) {
      console.error(`Failed to delete pomodoro session ${id}:`, error);
      throw error;
    }
  },
  
  // Get pomodoro settings for the current user
  getSettings: async () => {
    try {
      const response = await api.get('/pomodoro/settings');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch pomodoro settings:', error);
      throw error;
    }
  },
  
  // Save pomodoro settings
  saveSettings: async (settings) => {
    try {
      const response = await api.post('/pomodoro/settings', {
        focusDuration: settings.focusDuration,
        shortBreakDuration: settings.shortBreakDuration,
        longBreakDuration: settings.longBreakDuration,
        sessionsBeforeLongBreak: settings.sessionsBeforeLongBreak
      });
      return response.data;
    } catch (error) {
      console.error('Failed to save pomodoro settings:', error);
      throw error;
    }
  }
};

// Dashboard services - Batch data fetching
export const dashboardService = {
  getDashboardData: async () => {
    try {
      const response = await api.get('/dashboard');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      throw error;
    }
  }
};

// --- Add Subscription Service Here ---

export const subscriptionService = {
  getStatus: async () => {
    const response = await api.get('/subscriptions/status');
    return response.data; // Expected: { tier: string, isActive: bool, endDate: string | null }
  },
  createCheckoutSession: async () => {
    const response = await api.post('/subscriptions/create-checkout-session');
    return response.data; // Expected: { sessionId: string, publicKey: string }
  },
  cancelSubscription: async () => {
    const response = await api.post('/subscriptions/cancel');
    return response.data; // Expected: { message: string }
  },
  // Update subscription tier locally and notify app
  setSubscriptionTier: (tier) => {
    try {
      localStorage.setItem('subscriptionTier', tier);
      window.dispatchEvent(new CustomEvent('subscription-updated', { detail: { tier } }));
    } catch (_) {
      // ignore storage errors (e.g. quota)
    }
  },
  confirmSubscription: async (sessionId) => {
    const response = await api.post('/subscriptions/confirm', { sessionId });
    return response.data;
  },
};

export default api; 
